import { Dispatch, SetStateAction } from 'react';

import { Fade } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import Bird from '../../../../assets/images/bird.png';
import CatWithFish from '../../../../assets/images/cat-and-fish.png';
import CatInABox from '../../../../assets/images/cat-in-a-box.png';
import OtherCat from '../../../../assets/images/cat-other.png';
import Huskey from '../../../../assets/images/husky.png';
import Leash from '../../../../assets/images/leash-2.png';
import {
  ServiceType,
  SERVICE_TYPE_LABELS,
} from '../../../../happypaws-shared/src/Services/services';

const animalsOptions = [
  {
    id: 'dogs',
    label: 'Dogs',
    image: Huskey,
  },
  {
    id: 'cats',
    label: 'Cats',
    image: CatWithFish,
  },
  {
    id: 'other-animals',
    label: 'Other',
    image: Bird,
  },
];

const servicesOptions = [
  {
    id: ServiceType.PET_SITTING,
    label: SERVICE_TYPE_LABELS[ServiceType.PET_SITTING],
    image: CatInABox,
  },
  {
    id: ServiceType.PET_WALKING,
    label: SERVICE_TYPE_LABELS[ServiceType.PET_WALKING],
    image: Leash,
  },
  {
    id: ServiceType.PET_TRAINING,
    label: SERVICE_TYPE_LABELS[ServiceType.PET_TRAINING],
    image: Huskey,
  },
  {
    id: ServiceType.PET_DAYCARE,
    label: SERVICE_TYPE_LABELS[ServiceType.PET_DAYCARE],
    image: CatWithFish,
  },
  {
    id: ServiceType.OTHER_SERVICE,
    label: SERVICE_TYPE_LABELS[ServiceType.OTHER_SERVICE],
    image: OtherCat,
  },
];

export interface Step3State {
  serviceTypes: ServiceType[];
  petPreferences: string[];
}

interface StepTreeProps {
  currentStep: number;
  handleNext: () => void;
  handleBack: () => void;
  step3: Step3State;
  setStep3: Dispatch<SetStateAction<Step3State>>;
  errors: Record<string, string>;
  clearError: (key: string) => void;
}

const StepTree = (props: StepTreeProps) => {
  const { currentStep, step3, setStep3, handleNext, handleBack, errors, clearError } = props;

  return (
    <Fade in={currentStep === 3} timeout={600}>
      <Box sx={{ py: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontSize: '1.8rem',
            fontWeight: 700,
            color: '#178B8E',
            mb: 1,
            textAlign: 'center',
          }}
        >
          What services do you offer?
        </Typography>

        <Typography
          variant="body1"
          sx={{
            fontSize: '1rem',
            color: '#757575',
            mb: 5,
            textAlign: 'center',
          }}
        >
          Select the pet care services you provide so pet owners know exactly how you can help.
        </Typography>

        <Stack spacing={4}>
          {/* Service Types with Icons */}
          <Box>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>
              What kind of services do you offer?
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: '#178B8E', mb: 2 }}>
              You can select multiple choices!
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                width: '100%',
                gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                gap: 2,
                mb: 2,
              }}
            >
              {servicesOptions.map((service) => (
                <Box
                  key={service.id}
                  onClick={() => {
                    setStep3((prev) => ({
                      ...prev,
                      serviceTypes: prev.serviceTypes.includes(service.id)
                        ? prev.serviceTypes.filter((s) => s !== service.id)
                        : [...prev.serviceTypes, service.id],
                    }));
                    clearError('serviceTypes');
                  }}
                  sx={{
                    p: 2,
                    border: '2px solid',
                    borderColor: step3.serviceTypes.includes(service.id) ? '#178B8E' : '#E0E0E0',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    backgroundColor: step3.serviceTypes.includes(service.id)
                      ? '#F0F9F9'
                      : '#FFFFFF',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    '&:hover': {
                      borderColor: '#178B8E',
                      backgroundColor: '#F0F9F9',
                    },
                  }}
                >
                  <img
                    src={service.image}
                    alt={service.label}
                    style={{ width: 122, height: 122, borderRadius: '8px' }}
                  />
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      textAlign: 'center',
                    }}
                  >
                    {service.label}
                  </Typography>
                </Box>
              ))}
            </Box>

            {errors.serviceTypes && (
              <Typography variant="caption" color="error">
                {errors.serviceTypes}
              </Typography>
            )}

            {/* <TextField
              fullWidth
              placeholder="Write here"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#FFFFFF',
                  borderRadius: '8px',
                },
              }}
            /> */}
          </Box>

          {/* What animals do you work with */}
          <Box>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>
              What animals do you usually work with?
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: '#178B8E', mb: 2 }}>
              You can select multiple choices!
            </Typography>

            <Box sx={{ display: 'flex', gap: 2 }}>
              {animalsOptions.map((animal) => (
                <Box
                  key={animal.id}
                  onClick={() => {
                    setStep3((prev) => ({
                      ...prev,
                      petPreferences: prev.petPreferences.includes(animal.id)
                        ? prev.petPreferences.filter((p) => p !== animal.id)
                        : [...prev.petPreferences, animal.id],
                    }));
                    clearError('petPreferences');
                  }}
                  sx={{
                    flex: 1,
                    p: 2,
                    border: '2px solid',
                    borderColor: step3.petPreferences.includes(animal.id) ? '#178B8E' : '#E0E0E0',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    backgroundColor: step3.petPreferences.includes(animal.id)
                      ? '#F0F9F9'
                      : '#FFFFFF',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    '&:hover': {
                      borderColor: '#178B8E',
                      backgroundColor: '#F0F9F9',
                    },
                  }}
                >
                  <img
                    src={animal.image}
                    alt={animal.label}
                    style={{ width: 125, height: 125, borderRadius: '8px' }}
                  />
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      color: '#178B8E',
                    }}
                  >
                    {animal.label}
                  </Typography>
                </Box>
              ))}
            </Box>

            {errors.petPreferences && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                {errors.petPreferences}
              </Typography>
            )}
          </Box>

          {/* Home environment */}
          <Box>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>Home environment</Typography>
            <Typography sx={{ fontSize: '0.875rem', color: '#178B8E', mb: 2 }}>
              You can select multiple choices!
            </Typography>

            <Stack direction="row" spacing={2} flexWrap="wrap" mb={2} useFlexGap>
              {[
                'Has kids',
                'Has other pets',
                'Yard with fence',
                'Allowed on furniture',
                'Other',
              ].map((option) => (
                <FormControl key={option} component="fieldset">
                  <Box
                    onClick={() => {
                      const envId = option.toLowerCase().replace(/\s+/g, '-');
                      setStep3((prev) => ({
                        ...prev,
                        petPreferences: prev.petPreferences.includes(envId)
                          ? prev.petPreferences.filter((p) => p !== envId)
                          : [...prev.petPreferences, envId],
                      }));
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      cursor: 'pointer',
                    }}
                  >
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        border: '2px solid',
                        borderColor: step3.petPreferences.includes(
                          option.toLowerCase().replace(/\s+/g, '-')
                        )
                          ? '#178B8E'
                          : '#B8D8D8',
                        backgroundColor: step3.petPreferences.includes(
                          option.toLowerCase().replace(/\s+/g, '-')
                        )
                          ? '#178B8E'
                          : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {step3.petPreferences.includes(option.toLowerCase().replace(/\s+/g, '-')) && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: '#FFFFFF',
                          }}
                        />
                      )}
                    </Box>
                    <Typography>{option}</Typography>
                  </Box>
                </FormControl>
              ))}
            </Stack>

            <TextField
              fullWidth
              placeholder="Write here"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#FFFFFF',
                  borderRadius: '8px',
                },
              }}
            />
          </Box>
        </Stack>

        <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
          <Button
            fullWidth
            variant="outlined"
            size="large"
            onClick={handleBack}
            sx={{
              borderColor: '#178B8E',
              color: '#178B8E',
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: '8px',
              '&:hover': {
                borderColor: '#0F6164',
                backgroundColor: '#F0F9F9',
              },
            }}
          >
            Previous
          </Button>

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleNext}
            sx={{
              backgroundColor: '#178B8E',
              color: '#FFFFFF',
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: '#0F6164',
              },
            }}
          >
            Next
          </Button>
        </Box>
      </Box>
    </Fade>
  );
};

export default StepTree;
